import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, Home } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }

      try {
        const response = await axios.get(`${API}/bpo/checkout/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === "paid") {
          setStatus("success");
          setPaymentDetails(data);
          return;
        } else if (data.status === "expired") {
          setStatus("expired");
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error("Error checking payment status:", error);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        } else {
          setStatus("error");
        }
      }
    };

    pollPaymentStatus();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {status === "loading" && (
          <div className="animate-fade-in">
            <Loader2 className="w-16 h-16 mx-auto mb-6 animate-spin text-black" />
            <h1 className="font-heading text-3xl font-semibold mb-4">Processing Payment</h1>
            <p className="text-neutral-600">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === "success" && (
          <div className="animate-fade-in">
            <CheckCircle className="w-20 h-20 mx-auto mb-6 text-black" />
            <h1 className="font-heading text-4xl font-bold mb-4" data-testid="success-title">
              Payment Successful!
            </h1>
            <p className="text-neutral-600 mb-8">
              Thank you for your purchase. Your Broker Price Opinion request has been received. 
              We will contact you shortly to begin the process.
            </p>
            
            {paymentDetails && (
              <div className="bg-neutral-50 p-6 mb-8 text-left">
                <h3 className="font-accent uppercase tracking-wider text-sm mb-4">Order Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Service</span>
                    <span className="font-semibold">Broker Price Opinion</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Amount</span>
                    <span className="font-semibold">${(paymentDetails.amount_total / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Status</span>
                    <span className="font-semibold text-green-600">Paid</span>
                  </div>
                </div>
              </div>
            )}

            <Link
              to="/"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="back-home-btn"
            >
              <Home className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        )}

        {status === "error" && (
          <div className="animate-fade-in">
            <XCircle className="w-20 h-20 mx-auto mb-6 text-red-500" />
            <h1 className="font-heading text-3xl font-semibold mb-4">Something Went Wrong</h1>
            <p className="text-neutral-600 mb-8">
              We couldn't verify your payment. Please contact us if you were charged.
            </p>
            <Link to="/" className="btn-primary inline-flex items-center gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        )}

        {status === "expired" && (
          <div className="animate-fade-in">
            <XCircle className="w-20 h-20 mx-auto mb-6 text-neutral-400" />
            <h1 className="font-heading text-3xl font-semibold mb-4">Session Expired</h1>
            <p className="text-neutral-600 mb-8">
              Your payment session has expired. Please try again.
            </p>
            <Link to="/#services" className="btn-primary inline-flex items-center gap-2">
              Try Again
            </Link>
          </div>
        )}

        {status === "timeout" && (
          <div className="animate-fade-in">
            <Loader2 className="w-16 h-16 mx-auto mb-6 text-neutral-400" />
            <h1 className="font-heading text-3xl font-semibold mb-4">Payment Processing</h1>
            <p className="text-neutral-600 mb-8">
              Your payment is still being processed. Please check your email for confirmation.
            </p>
            <Link to="/" className="btn-primary inline-flex items-center gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
